import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { BuildingTwoIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import {
  assignCenterTeachingHall,
  unassignCenterTeachingHall
} from '../../../api/centers/center-teaching-hall/assign-center-teaching-hall'
import { getCenterTeachingHalls } from '../../../api/centers/center-teaching-hall/get-center-teaching-hall'
import { getCenter } from '../../../api/centers/get-center'
import { getTeachingHalls } from '../../../api/ols/teaching-halls/get-teaching-halls'
import Form, { FormControl, FormField, FormItem } from '../../../components/elements/forms/form'
import ControlledMultiSelectInput from '../../../components/elements/inputs/controlled-multi-select-input'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import ValidationButtons from '../../../components/elements/validation-buttons'
import { Paths } from '../../../lib/constants/path'
import { useObserver } from '../../../lib/contexts/observer'
import { useDataProps } from '../../../lib/hooks/use-data-props'
import useTranslate from '../../../lib/hooks/use-translate'
import { Center } from '../../../lib/types/models/center/center'
import { CenterProperties } from '../../../lib/types/models/center/properties.center'
import { DataProps } from '../../../lib/types/utils'
import { twoButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { createNumberArraySchema } from '../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { constructOlPersonalSpaceCenterPath } from '../../ol-personal-space/centers/utils/functions'

type FormData = {
  teachingHallIds: number[]
}

type Props = {
  isOpen: boolean
  onCancel: () => void
}

export function ChangeHostFamilyModel(props: DataProps<Center, Props>) {
  const { id, data: center, isOpen, onCancel } = useDataProps(props, (id) => getCenter(id))
  const { t } = useTranslate()
  const olId = center?.ol?.id
  const { navigate } = useNavigator()
  const olTeachingHallCrud = useObserver({ key: 'ol-teaching-hall-crud' })

  const form = useForm<FormData>({
    resolver: zodResolver(z.object({ teachingHallIds: createNumberArraySchema('Teaching Hall') })),
    defaultValues: { teachingHallIds: [] },
    mode: FORM_MODE
  })

  const { data: centerTeachingHalls, isPending: isCenterTeachingHallsPending } = useQuery({
    queryKey: ['center-teaching-halls', id],
    queryFn: () => getCenterTeachingHalls(id)
  })

  const { data: teachingHalls, isPending: isTeachingHallsPending } = useQuery({
    queryKey: ['teaching-halls'],
    queryFn: () => {
      if (!olId) return
      return getTeachingHalls(olId)
    },
    enabled: !!olId
  })

  const assignTeachingHallMutation = useMutation({
    mutationFn: assignCenterTeachingHall,
    onError: showAxiosError
  })

  const unassignTeachingHallMutation = useMutation({
    mutationFn: unassignCenterTeachingHall,
    onError: showAxiosError
  })

  async function handleSubmit(data: FormData) {
    // delete all previous teaching halls
    if (teachingHalls?.data) {
      await Promise.all(
        teachingHalls.data.map((value) =>
          unassignTeachingHallMutation.mutateAsync({ centerId: id, teachingHallId: value.id })
        )
      )
    }

    // then set the new teaching halls
    if (data.teachingHallIds.length > 0) {
      await Promise.all(
        data.teachingHallIds.map((value) =>
          assignTeachingHallMutation.mutateAsync({ centerId: id, teachingHallId: value })
        )
      )
    }

    toast.success('Teaching Hall assigned successfully')
    olTeachingHallCrud.notify(['ol-teaching-hall-table'])
    onCancel()
  }

  useEffect(() => {
    if (centerTeachingHalls) {
      form.reset({
        teachingHallIds: centerTeachingHalls.data.map((teachingHall) => teachingHall.id)
      })
    }
  }, [centerTeachingHalls])

  return (
    <Modal
      icon={BuildingTwoIcon}
      isOpen={isOpen}
      title={t('ol.center.placement.change.host.family')}
      onCancel={onCancel}
      hideActions
    >
      {!olId ? (
        <Typography variant="label-medium">{t('no.ol')}</Typography>
      ) : !center || isCenterTeachingHallsPending || isTeachingHallsPending ? (
        <LoadingScreen />
      ) : (
        <div className={cn(MODAL_CONTENT_CLASSNAME, 'flex flex-col gap-3')}>
          <div className="flex flex-row items-center gap-2">
            <Badge.Link
              text={center.name.toLocaleUpperCase()}
              color={tailwindColors.white.active}
              textColor={tailwindColors.primary.DEFAULT}
              onClick={() =>
                navigate(constructOlPersonalSpaceCenterPath(Paths.OL_CENTER_PLACEMENTS, center.id))
              }
            />

            <Badge text={CenterProperties.getCenterDates(center)} />
          </div>

          <Form form={form}>
            <form className={'flex flex-col gap-4'} onSubmit={form.handleSubmit(handleSubmit)}>
              <FormField
                control={form.control}
                name={'teachingHallIds'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledMultiSelectInput
                        {...field}
                        variant="primary"
                        dropDownPosition="above"
                        label={t('ol.center.placement.change.host.family.label')}
                        options={teachingHalls?.data.map((teachingHall) => ({
                          label: teachingHall.name,
                          value: teachingHall.id
                        }))}
                        selectedOptions={field.value?.map((teachingHall) => ({
                          label:
                            teachingHalls?.data.find((th) => th.id === teachingHall)?.name ?? '',
                          value: teachingHall
                        }))}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <ValidationButtons
                buttonsConfig={twoButtonsConfig({
                  onCancel: onCancel,
                  onConfirm: handleSubmit,
                  isEnglish: true
                })}
              />
            </form>
          </Form>
        </div>
      )}
    </Modal>
  )
}
